import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Moment } from 'moment';
import { uniq as _uniq } from 'lodash';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { confirm } from 'devextreme/ui/dialog';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxLoadIndicatorComponent } from 'devextreme-angular/ui/load-indicator';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import {
    DxiColumnComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoPagerComponent,
    DxoPagingComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import {
    HeaderContentService,
    INSPECTION_STATUS_LABELS,
    InspectionStatus,
    InventoryProduct,
    LnwshopSaleService,
    LnwshopSaleState,
    MessageType,
    NotificationService,
    PACKAGE_SOURCE_LABELS,
    PackageSource,
    ProductLookupService,
    ReturnFetchReport,
    ReturnInspectionService,
    ReturnInspectionsDoc,
    ReturnOrdersService,
    ReturnPackage,
    ReturnReceiptService,
    ReturnReceiptsDoc,
    RestockAssessment,
    UtilService,
    assessRestock,
    inspectionKey,
    inspectionStatus,
    receiptFor,
} from 'src/app/shared/services';
import { STORES } from 'src/app/shared/config/store-config';
import { ProductCardPopupComponent } from 'src/app/shared/components/product-card-popup/product-card-popup.component';
import { ReturnDetailPopupComponent } from 'src/app/shared/components/return-detail-popup/return-detail-popup.component';
import { FormatRelativeDatetimePipe } from 'src/app/shared/pipes/format-relative-datetime.pipe';
import { ThaiDateTimePipe } from 'src/app/shared/pipes/thai-datetime.pipe';

// One item line of one received return package, with what the shop has
// recorded about it. The grid is per item, not per return, because each
// item is inspected and keyed on its own.
interface InspectionRow {
    key: string;
    returnRowId: string;
    storeTitle: string;
    // คืนสินค้า / ตีกลับ — see PackageSource.
    source: PackageSource;
    sourceLabel: string;
    orderNo: string;
    returnId: string;
    trackingNumber: string;
    receivedAt: string | null;
    receivedAtDate: Date | null;
    sku: string;
    // From the platform when it names the item, else from the lnwshop
    // inventory once the SKU has been resolved.
    name: string;
    qty: number;
    restocked: number;
    writtenOff: number;
    note: string;
    status: InspectionStatus;
    statusLabel: string;
    markedAt: string | null;
    markedBy: string;
    // Where stock for this SKU is keyed by hand.
    adminUrl: string;
    // Why this package is keyed in by hand (see lnwshop-restock.ts).
    restockNote: string;
    // Marked by the shop, then restored by lnwshop too: to deduct.
    doubleCount: boolean;
}

const DEFAULT_LOOKBACK_DAYS = 60;

@Component({
    selector: 'app-return-inspection',
    templateUrl: './return-inspection.component.html',
    styleUrls: ['./return-inspection.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxLoadIndicatorComponent,
        DxCheckBoxComponent,
        DxDataGridComponent,
        DxNumberBoxComponent,
        DxPopupComponent,
        DxTextBoxComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoHeaderFilterComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxTemplateDirective,
        ProductCardPopupComponent,
        ReturnDetailPopupComponent,
        FormatRelativeDatetimePipe,
        ThaiDateTimePipe,
    ],
})
export class ReturnInspectionComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private cdref = inject(ChangeDetectorRef);
    private headerContentService = inject(HeaderContentService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private notificationService = inject(NotificationService);
    private returnOrdersService = inject(ReturnOrdersService);
    private returnReceiptService = inject(ReturnReceiptService);
    private returnInspectionService = inject(ReturnInspectionService);
    private productLookupService = inject(ProductLookupService);
    private lnwshopSaleService = inject(LnwshopSaleService);
    private utilService = inject(UtilService);

    @ViewChild('productCard', { static: true })
    private productCard!: ProductCardPopupComponent;

    @ViewChild('detailPopup', { static: true })
    private detailPopup!: ReturnDetailPopupComponent;

    // The page title, projected into the app header's centre slot.
    @ViewChild('headerMenu', { static: true })
    private headerMenu!: TemplateRef<unknown>;

    lookbackDays = DEFAULT_LOOKBACK_DAYS;
    loading = false;
    // Every return with a package, as fetched; only the received ones make
    // rows — an item cannot be inspected before it is in the building —
    // and of those only the ones lnwshop does not restock by itself (see
    // lnwshop-restock.ts): keying those in would count them twice.
    private returns: ReturnPackage[] = [];
    // lnwshop's own view of each received order, from its nightly crawl.
    // Until it answers, the platform rules alone decide what is shown.
    private saleStates: Record<string, LnwshopSaleState> = {};
    lnwshopCrawledAt: Moment | null = null;
    checkingLnwshop = false;
    // A lookup failed: the hidden packages were judged by the rules alone.
    lnwshopCheckFailed = false;
    // Lookups belong to the load() that started them (an older one's answer
    // is dropped), and each order is asked for once per load.
    private lnwshopGeneration = 0;
    private lnwshopAsked = new Set<string>();
    private lnwshopInFlight = 0;
    private fetchDone = false;
    // Received packages hidden because lnwshop restores them itself.
    hiddenAutoPackages = 0;
    // Rows the shop marked that lnwshop then restored as well.
    doubleCountedRows = 0;
    reports: ReturnFetchReport[] = [];
    private receipts: ReturnReceiptsDoc = { receipts: {} };
    private inspections: ReturnInspectionsDoc = { items: {} };
    // Marks are refused until the stored document has loaded — see
    // return-packages.component.ts for the reasoning.
    inspectionsLoaded = false;
    saving = false;
    private products = new Map<string, InventoryProduct>();
    onlyPending = true;
    private rows: InspectionRow[] = [];
    dataSource: InspectionRow[] = [];

    // ---- Split / note popup ----
    editPopupVisible = false;
    editRow: InspectionRow | null = null;
    editRestocked = 0;
    editWrittenOff = 0;
    editNote = '';

    ngOnInit(): void {
        this.load();
    }

    ngAfterViewInit(): void {
        this.headerContentService.set(this.headerMenu);
    }

    ngOnDestroy(): void {
        this.headerContentService.clear(this.headerMenu);
    }

    backToReturns(): void {
        this.router.navigate(['/secure/orders/returns']);
    }

    load(): void {
        if (this.loading) {
            return;
        }
        this.loading = true;
        this.fetchDone = false;
        this.returns = [];
        this.reports = [];
        this.saleStates = {};
        this.lnwshopCrawledAt = null;
        this.lnwshopCheckFailed = false;
        this.lnwshopGeneration++;
        this.lnwshopAsked.clear();
        this.lnwshopInFlight = 0;
        this.checkingLnwshop = false;
        this.rebuild();
        this.returnReceiptService
            .getDoc()
            .pipe(
                catchError((error: unknown) => {
                    this.notificationService.pushMessage({
                        message: `โหลดข้อมูลการรับพัสดุไม่ได้: ${this.utilService.errorToString(error)}`,
                        messageType: MessageType.error,
                    });
                    return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((receipts) => {
                if (receipts) {
                    this.receipts = receipts;
                }
                this.rebuild();
                this.checkLnwshop();
            });
        this.returnInspectionService
            .getDoc()
            .pipe(
                catchError((error: unknown) => {
                    this.notificationService.pushMessage({
                        message: `โหลดผลตรวจสอบไม่ได้: ${this.utilService.errorToString(error)}`,
                        messageType: MessageType.error,
                    });
                    return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((inspections) => {
                if (inspections) {
                    this.inspections = inspections;
                    this.inspectionsLoaded = true;
                }
                this.rebuild();
            });
        const storeRows = new Map<string, ReturnPackage[]>();
        this.returnOrdersService
            .load(this.lookbackDays)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    // No package-stage filter here: rows are made only for
                    // returns the shop confirmed received (see rebuild), and
                    // a confirmed parcel counts whatever the platform says —
                    // e.g. a TikTok case it closed without tracking. A store
                    // can arrive twice (the index, then its live top-up);
                    // each arrival replaces its rows.
                    const storeId = result.report.storeId;
                    const repeat = storeRows.has(storeId);
                    storeRows.set(storeId, result.rows);
                    this.returns = [...storeRows.values()].flat();
                    this.reports = [
                        ...this.reports.filter((r) => r.storeId !== storeId),
                        result.report,
                    ];
                    this.rebuild();
                    this.resolveProducts(result.rows);
                    if (result.report.error && !repeat) {
                        this.notificationService.pushMessage({
                            message: `${result.report.title}: ${result.report.error}`,
                            messageType: MessageType.error,
                        });
                    }
                },
                error: (error: unknown) => {
                    this.loading = false;
                    this.notificationService.pushMessage({
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                    this.cdref.markForCheck();
                },
                complete: () => {
                    this.loading = false;
                    this.fetchDone = true;
                    this.checkLnwshop();
                    this.cdref.markForCheck();
                },
            });
    }

    // Asks lnwshop's crawl about every received order not asked yet in this
    // load, once the returns are in (receipts may land before or after).
    private checkLnwshop(): void {
        if (!this.fetchDone) {
            return;
        }
        const orderNos = _uniq(
            this.returns
                .filter((ret) => !!receiptFor(ret, this.receipts))
                .map((ret) => ret.orderNo)
                .filter((orderNo) => !this.lnwshopAsked.has(orderNo)),
        );
        if (orderNos.length === 0) {
            return;
        }
        const generation = this.lnwshopGeneration;
        orderNos.forEach((orderNo) => this.lnwshopAsked.add(orderNo));
        this.lnwshopInFlight++;
        this.checkingLnwshop = true;
        this.lnwshopSaleService
            .lookup(orderNos)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((lookup) => {
                // An answer for a load since replaced.
                if (generation !== this.lnwshopGeneration) {
                    return;
                }
                this.lnwshopInFlight--;
                this.checkingLnwshop = this.lnwshopInFlight > 0;
                if (lookup.failed) {
                    this.lnwshopCheckFailed = true;
                    this.notificationService.pushMessage({
                        message:
                            'ตรวจกับข้อมูล lnwshop ไม่ได้ — ใช้เกณฑ์ของแพลตฟอร์มแทน รายการที่ซ่อนอาจมีที่ต้องเพิ่มสต็อกเอง',
                        messageType: MessageType.warning,
                    });
                }
                this.saleStates = { ...this.saleStates, ...lookup.states };
                this.lnwshopCrawledAt =
                    lookup.crawledAt ?? this.lnwshopCrawledAt;
                this.rebuild();
            });
    }

    get storeCount(): number {
        return STORES.length;
    }

    get rowCount(): number {
        return this.rows.length;
    }

    get pendingCount(): number {
        return this.rows.filter((row) => row.status !== 'done').length;
    }

    get restockedUnits(): number {
        return this.rows.reduce((sum, row) => sum + row.restocked, 0);
    }

    get writtenOffUnits(): number {
        return this.rows.reduce((sum, row) => sum + row.writtenOff, 0);
    }

    get failedReports(): ReturnFetchReport[] {
        return this.reports.filter((report) => !!report.error);
    }

    onFilterChanged(): void {
        this.rebuild();
    }

    openProduct(sku: string): void {
        this.productCard.open(sku);
    }

    // The package an item came in, from the type badge.
    openDetail(row: InspectionRow, event: Event): void {
        event.stopPropagation();
        const pkg = this.returns.find((ret) => ret.id === row.returnRowId);
        if (pkg) {
            // What this page already knows, so the popup shows the same
            // verdict even if its own check fails.
            this.detailPopup.open(pkg, receiptFor(pkg, this.receipts), {
                sale: this.saleStates[pkg.orderNo],
                crawledAt: this.lnwshopCrawledAt,
            });
        }
    }

    // Every unit back on the shelf.
    restockAll(row: InspectionRow): void {
        this.save(row, { restocked: row.qty, writtenOff: 0, note: row.note });
    }

    // Every unit disposed of.
    writeOffAll(row: InspectionRow): void {
        this.save(row, { restocked: 0, writtenOff: row.qty, note: row.note });
    }

    openEdit(row: InspectionRow): void {
        this.editRow = row;
        this.editRestocked = row.restocked;
        this.editWrittenOff = row.writtenOff;
        this.editNote = row.note;
        this.editPopupVisible = true;
    }

    get editTotal(): number {
        return (this.editRestocked || 0) + (this.editWrittenOff || 0);
    }

    // More units than came back is a typo, never a fact.
    get editOverflow(): boolean {
        return !!this.editRow && this.editTotal > this.editRow.qty;
    }

    saveEdit(): void {
        if (!this.editRow || this.editOverflow) {
            return;
        }
        const row = this.editRow;
        this.editPopupVisible = false;
        this.save(row, {
            restocked: this.editRestocked || 0,
            writtenOff: this.editWrittenOff || 0,
            note: this.editNote,
        });
    }

    async clearMark(row: InspectionRow): Promise<void> {
        if (!this.canSave()) {
            return;
        }
        const ok = await confirm(
            `ล้างผลตรวจสอบของ ${row.sku} (คำสั่งซื้อ ${row.orderNo})?`,
            'ล้างผลตรวจสอบ',
        );
        if (!ok) {
            return;
        }
        this.saving = true;
        this.returnInspectionService
            .clear(row.key)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => this.applyDoc(doc),
                error: (error: unknown) => this.saveFailed(error),
            });
    }

    onRowPrepared(event: any): void {
        if (event.rowType !== 'data' || !event.data) {
            return;
        }
        const row: InspectionRow = event.data;
        if (row.doubleCount) {
            event.rowElement.classList.add('double-count-row');
        } else if (row.status === 'done') {
            event.rowElement.classList.add('done-row');
        }
    }

    private save(
        row: InspectionRow,
        mark: { restocked: number; writtenOff: number; note: string },
    ): void {
        if (!this.canSave()) {
            return;
        }
        this.saving = true;
        this.returnInspectionService
            .set(row.key, mark)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => {
                    this.applyDoc(doc);
                    this.notificationService.pushMessage({
                        message: `บันทึกแล้ว: ${row.sku} เข้าสต็อก ${mark.restocked} · ตัดจำหน่าย ${mark.writtenOff}`,
                        messageType: MessageType.success,
                    });
                },
                error: (error: unknown) => this.saveFailed(error),
            });
    }

    private applyDoc(doc: ReturnInspectionsDoc): void {
        this.inspections = doc;
        this.saving = false;
        this.rebuild();
    }

    private canSave(): boolean {
        if (!this.inspectionsLoaded) {
            this.notificationService.pushMessage({
                message: 'ยังโหลดผลตรวจสอบไม่ได้ — โหลดใหม่ก่อนบันทึก',
                messageType: MessageType.warning,
            });
            return false;
        }
        return !this.saving;
    }

    private saveFailed(error: unknown): void {
        this.saving = false;
        this.notificationService.pushMessage({
            message: `บันทึกไม่ได้: ${this.utilService.errorToString(error)}`,
            messageType: MessageType.error,
        });
        this.cdref.markForCheck();
    }

    // Lazada names nothing but the SKU, and the lnwstore link needs the
    // product id, so every SKU is resolved against the inventory in one
    // batch per store.
    private resolveProducts(returns: ReturnPackage[]): void {
        const skus = returns.flatMap((ret) =>
            ret.items.map((item) => item.sku),
        );
        if (skus.length === 0) {
            return;
        }
        this.productLookupService
            .findBySkus(skus)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (found) => {
                    found.forEach((product, sku) =>
                        this.products.set(sku, product),
                    );
                    this.rebuild();
                },
                // Names and links are a convenience; the rows stand without
                // them, so a failed lookup is logged, not shown.
                error: (error: unknown) =>
                    console.warn('product lookup failed', error),
            });
    }

    // The package's lnwshop verdict: its crawl's answer where there is one,
    // the platform rule until then.
    private restockOf(ret: ReturnPackage): RestockAssessment {
        return assessRestock(
            ret,
            this.saleStates[ret.orderNo],
            this.lnwshopCrawledAt,
        );
    }

    private rebuild(): void {
        let hidden = 0;
        let doubleCounted = 0;
        this.rows = this.returns.flatMap((ret) => {
            const receipt = receiptFor(ret, this.receipts);
            if (!receipt) {
                return [];
            }
            const restock = this.restockOf(ret);
            // A package the shop already recorded stays, whatever lnwshop
            // does: once marked it has been handled, and if lnwshop has now
            // restored it as well, those units are counted twice — the one
            // row that most needs to be seen.
            const marked = ret.items.some(
                (item) =>
                    !!this.inspections.items[inspectionKey(ret.id, item.sku)],
            );
            if (restock.verdict !== 'manual' && !marked) {
                hidden++;
                return [];
            }
            const restoredToo = restock.verdict === 'restored';
            return ret.items.map((item): InspectionRow => {
                const key = inspectionKey(ret.id, item.sku);
                const mark = this.inspections.items[key];
                const status = inspectionStatus(mark, item.qty);
                const product = this.products.get(item.sku);
                const handled =
                    (mark?.restocked ?? 0) + (mark?.writtenOff ?? 0);
                const doubleCount = restoredToo && handled > 0;
                if (doubleCount) {
                    doubleCounted++;
                }
                return {
                    key,
                    returnRowId: ret.id,
                    storeTitle: ret.storeTitle,
                    source: ret.source,
                    sourceLabel: PACKAGE_SOURCE_LABELS[ret.source],
                    orderNo: ret.orderNo,
                    returnId: ret.returnId,
                    trackingNumber: ret.trackingNumber,
                    receivedAt: receipt.at,
                    receivedAtDate: receipt.at ? new Date(receipt.at) : null,
                    sku: item.sku,
                    name: item.name || product?.title || '',
                    qty: item.qty,
                    restocked: mark?.restocked ?? 0,
                    writtenOff: mark?.writtenOff ?? 0,
                    note: mark?.note ?? '',
                    status,
                    statusLabel: INSPECTION_STATUS_LABELS[status],
                    markedAt: mark?.at ?? null,
                    markedBy: mark?.by ?? '',
                    adminUrl: product?.adminUrl ?? '',
                    // lnwshop put back every unit of the line; the ones the
                    // shop keyed in (restocked) or found unsellable (written
                    // off) are now in lnwshop stock twice or wrongly.
                    restockNote: doubleCount
                        ? `lnwshop คืนสต็อกให้แล้วด้วย — หักออกใน lnwshop ${handled} ชิ้น แล้วล้างผลตรวจสอบของรายการนี้`
                        : restock.note,
                    doubleCount,
                };
            });
        });
        this.hiddenAutoPackages = hidden;
        this.doubleCountedRows = doubleCounted;
        this.dataSource = this.onlyPending
            ? this.rows.filter(
                  (row) => row.status !== 'done' || row.doubleCount,
              )
            : this.rows;
        this.cdref.markForCheck();
    }
}
